import { motion } from '../animation/Motion';
import { waitAnim } from '../../ui/dom';
import { t } from '../../i18n';

export type TransitionKind = 'page' | 'page-back' | 'cover' | 'fade' | 'none';

/**
 * DOM transitions between scenes. Only transform/opacity are animated so the
 * browser can composite them on the GPU without relayout.
 */
export async function runTransition(
  kind: TransitionKind,
  layer: HTMLElement,
  overlay: HTMLElement,
  oldDom: HTMLElement | null,
  newDom: HTMLElement,
  swap: () => void,
): Promise<void> {
  if (!oldDom || kind === 'none') {
    swap();
    return;
  }
  if (motion.reduced || kind === 'fade') {
    newDom.classList.add('fade-in');
    swap();
    oldDom.classList.add('fade-out');
    await waitAnim(oldDom, 400);
    newDom.classList.remove('fade-in');
    return;
  }
  if (kind === 'page' || kind === 'page-back') {
    // The old screen is a page being turned over to reveal the new one below
    // (or, going back, the new page turns over on top of the old one).
    swap();
    const flipping = kind === 'page' ? oldDom : newDom;
    const still = kind === 'page' ? newDom : oldDom;
    still.classList.add('page-under');
    flipping.classList.add(kind === 'page' ? 'page-turn-out' : 'page-turn-in');
    layer.classList.add('flipping');
    await waitAnim(flipping, 1100);
    layer.classList.remove('flipping');
    still.classList.remove('page-under');
    flipping.classList.remove('page-turn-out', 'page-turn-in');
    return;
  }
  // 'cover': the notebook cover closes over the old screen, then opens on the new one.
  const cover = document.createElement('div');
  cover.className = 'notebook-cover closing';
  cover.innerHTML =
    `<div class="cover-label"><span class="cover-title">Paper Legends</span><span class="cover-sub">${t('Name: ______')} &nbsp; ${t('Class: ___')}</span></div><div class="cover-elastic"></div>`;
  overlay.appendChild(cover);
  await waitAnim(cover, 900);
  swap();
  cover.classList.remove('closing');
  cover.classList.add('opening');
  await waitAnim(cover, 1200);
  cover.remove();
}
